import { Component, Input, AfterViewInit, ViewChild, ElementRef, forwardRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import SignaturePad from 'signature_pad';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-form-signature',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section" [attr.name]="name">
      <label *ngIf="label" [class.required-field]="required">{{ label }}</label>
      <div class="signature-container" [style.width.px]="width" [style.height.px]="height">
        <canvas #signatureCanvas class="signature-canvas" id="{{ id }}"></canvas>
      </div>
      <div class="signature-button-group">
        <button
          type="button"
          class="complete-signature"
          (click)="completeSignature()"
          [disabled]="isDisabled">
          {{ 'form.signature.complete_button' | translate }}
        </button>
        <button
          type="button"
          class="clear-signature"
          (click)="clearSignature()">
          {{ 'form.signature.clear_button' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .form-section {
      margin-bottom: 15px;
    }
    label {
      display: block;
      margin-bottom: 5px;
      font-weight: bold;
    }
    .signature-container {
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #fff;
      position: relative;
      min-height: 150px;
      overflow: hidden;
    }
    .signature-canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      touch-action: none;
    }
    .signature-button-group {
      display: flex;
      gap: 10px;
      margin-top: 8px;
    }
    button {
      padding: 8px 15px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
      border: 1px solid #ddd;
      background-color: #f5f5f5;
    }
    button.complete-signature {
      background-color: #4caf50;
      color: white;
      border-color: #4caf50;
    }
    button.clear-signature {
      background-color: #f44336;
      color: white;
      border-color: #f44336;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .completed {
      background-color: #f9f9f9;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormSignatureComponent),
      multi: true
    }
  ]
})
export class FormSignatureComponent implements AfterViewInit, ControlValueAccessor, OnDestroy {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() width: number = 500; // 默認寬度
  @Input() height: number = 150; // 默認高度
  @Input() required: boolean = false; // 是否必填

  // 按鈕文字屬性
  @Input() completeButtonText: string = '完成簽名';
  @Input() clearButtonText: string = '重新簽名';

  @ViewChild('signatureCanvas') signatureCanvasRef!: ElementRef<HTMLCanvasElement>;

  private signaturePad!: SignaturePad;
  private _signature: string | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private aspectRatio: number = 0;

  isDisabled: boolean = false;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private i18nService: I18nService) {}

  writeValue(value: any): void {
    this._signature = value;

    // 如果已初始化畫布且有簽名數據，則恢復簽名
    if (this.signaturePad && value) {
      // 創建臨時圖像來恢復簽名
      const image = new Image();
      image.onload = () => {
        this.resetCanvas();
        // 在畫布上繪製圖像
        const canvas = this.signatureCanvasRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          // 標記為已完成
          this.isDisabled = true;
          this.signatureCanvasRef.nativeElement.style.pointerEvents = 'none';
          this.signatureCanvasRef.nativeElement.classList.add('completed');
        }
      };
      image.src = value;
    } else if (this.signaturePad) {
      this.clearSignature();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.signatureCanvasRef) {
      this.signatureCanvasRef.nativeElement.style.pointerEvents = isDisabled ? 'none' : 'auto';
    }
  }

  ngAfterViewInit() {
    // 初始化畫布
    this.initializeCanvas();

    // 使用 ResizeObserver 監控大小變化
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateCanvasSize();
      });
      this.resizeObserver.observe(this.signatureCanvasRef.nativeElement.parentElement as HTMLElement);
    } else {
      // 降級方案：使用視窗 resize 事件
      window.addEventListener('resize', this.onWindowResize);
    }

    // 如果有初始簽名數據，恢復它
    if (this._signature) {
      this.writeValue(this._signature);
    }
  }

  ngOnDestroy() {
    // 清理 ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // 移除事件監聽器
    window.removeEventListener('resize', this.onWindowResize);
  }

  private onWindowResize = () => {
    this.updateCanvasSize();
  };

  private initializeCanvas() {
    if (!this.signatureCanvasRef) return;

    const canvas = this.signatureCanvasRef.nativeElement;

    // 設置初始畫布尺寸
    canvas.width = this.width;
    canvas.height = this.height;

    // 記錄寬高比
    this.aspectRatio = canvas.width / canvas.height;

    // 初始化 SignaturePad
    this.signaturePad = new SignaturePad(canvas);

    // 設置簽名事件監聽
    this.signaturePad.addEventListener('endStroke', () => {
      this.updateSignature();
    });
  }

  updateCanvasSize() {
    if (!this.signaturePad || !this.signatureCanvasRef) return;

    const canvas = this.signatureCanvasRef.nativeElement;
    const container = canvas.parentElement as HTMLElement;

    // 保存當前簽名數據
    const signatureData = !this.signaturePad.isEmpty() ? this.signaturePad.toDataURL() : null;

    // 設置新的畫布尺寸
    canvas.width = this.width; // 使用固定寬度
    canvas.height = this.height; // 使用固定高度

    // 重置簽名畫布
    this.signaturePad.clear();

    // 如果之前有簽名，恢復它
    if (signatureData) {
      const image = new Image();
      image.onload = () => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        }
      };
      image.src = signatureData;
    }
  }

  resetCanvas() {
    if (!this.signaturePad) return;

    // 清除畫布
    this.signaturePad.clear();

    // 重設畫布尺寸
    const canvas = this.signatureCanvasRef.nativeElement;
    canvas.width = this.width;
    canvas.height = this.height;
  }

  completeSignature() {
    if (this.signaturePad.isEmpty()) {
      const warningMessage = this.i18nService.translate('form.signature.empty_warning');
      console.warn(warningMessage);
      return;
    }

    // 更新簽名數值
    this.updateSignature();

    // 禁用畫布互動
    this.signatureCanvasRef.nativeElement.style.pointerEvents = 'none';
    this.isDisabled = true;

    // 加入完成後的樣式
    this.signatureCanvasRef.nativeElement.classList.add('completed');
  }

  clearSignature() {
    if (this.signaturePad) {
      this.signaturePad.clear();

      // 重新啟用畫布互動
      this.signatureCanvasRef.nativeElement.style.pointerEvents = 'auto';
      this.isDisabled = false;

      // 移除完成樣式
      this.signatureCanvasRef.nativeElement.classList.remove('completed');

      // 更新簽名值為 null
      this._signature = null;
      this.onChange(null);
      this.onTouched();
    }
  }

  updateSignature() {
    if (!this.signaturePad) return;

    let sigData: string | null = null;

    if (!this.signaturePad.isEmpty()) {
      // 獲取簽名數據為 PNG 格式
      sigData = this.signaturePad.toDataURL('image/png');
    }

    this._signature = sigData;
    this.onChange(sigData);
    this.onTouched();
  }
}
